import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";

// Copy supplied verbatim by the owner; aligned 2026-10-02 with the finalized
// Five Voices Framework: NOTICE → LISTEN → UNDERSTAND → RESPOND → HONOR.
// Do not add pricing, enrollment dates, accreditation, CE credits, testimonials,
// statistics, Sign In / Renew Certification buttons, or CCCHAPS branding.

const ctaCls =
  "inline-flex items-center gap-2 bg-navy text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200 whitespace-nowrap";
const ctaGoldCls =
  "inline-flex items-center gap-2 bg-gold text-navy px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-gold-hover transition-colors duration-200 whitespace-nowrap";

function Hero() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-20 sm:pt-44">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          From Diagnosis to Goodbye Facilitator Program
        </p>
        <h1 className="mt-5 max-w-4xl font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.08]">
          Become a Certified From Diagnosis to Goodbye Facilitator
        </h1>
        <p className="mt-6 max-w-3xl font-serif italic text-xl sm:text-2xl text-gold">
          Help people find their voice without becoming the loudest voice in
          the room.
        </p>
        <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            From Diagnosis to Goodbye was created to help patients, families,
            caregivers, professionals, and communities hear the human voices
            that can become lost during serious illness, caregiving, end of
            life, and grief.
          </p>
          <p>
            The Certified From Diagnosis to Goodbye Facilitator program
            prepares individuals to responsibly guide conversations, groups,
            workshops, and learning experiences using the principles and
            approved resources of From Diagnosis to Goodbye.
          </p>
          <p>
            This is more than learning how to lead a discussion. It is
            learning how to create space for another person's experience
            without taking it over.
          </p>
        </div>
        <Link
          to="/facilitators/training"
          data-testid="facilitators-cta-hero"
          className={`${ctaGoldCls} mt-10`}
        >
          Explore Facilitator Training <ArrowRight className="w-4 h-4" />
        </Link>
      </Reveal>

      <Reveal delay={0.1}>
        <div
          className="mt-16 bg-navy-deep rounded-md px-8 sm:px-14 py-14 sm:py-20 text-center"
          data-testid="facilitators-hero-statement"
        >
          <p className="max-w-3xl mx-auto font-serif italic text-2xl sm:text-4xl leading-snug text-ivory">
            &ldquo;The facilitator's voice should never become the{" "}
            <em className="text-gold">loudest voice</em> in the room.&rdquo;
          </p>
        </div>
      </Reveal>
    </section>
  );
}

const VOICES = [
  {
    n: "01",
    title: "The Patient's Voice",
    quote: "This is happening to me.",
    body: "The person experiencing illness remains a person—not simply a diagnosis, condition, prognosis, or care plan.",
  },
  {
    n: "02",
    title: "The Family's Voice",
    quote: "This is happening to someone I love.",
    body: "Families may carry fear, hope, uncertainty, exhaustion, grief, questions, and difficult decisions while trying to support someone they love.",
  },
  {
    n: "03",
    title: "The Caregiver's Voice",
    quote: "I am trying to hold everything together.",
    body: "Behind the practical responsibilities of caregiving may be exhaustion, changing roles, anticipatory grief, uncertainty, and loss.",
  },
  {
    n: "04",
    title: "The Professional or Helper's Voice",
    quote: "I am here to help.",
    body: "Healthcare professionals, chaplains, pastors, volunteers, facilitators, and other helpers bring valuable knowledge and support—but expertise should inform the room without owning the room.",
  },
  {
    n: "05",
    title: "The Voice of Grief",
    quote: "Something is changing, and I am already losing something.",
    body: "Grief can begin long before a death. Illness may bring losses involving independence, identity, relationships, roles, plans, abilities, home, and the future someone expected to have.",
  },
];

function Voices() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Voices in the Journey
          </p>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight">
            Every serious-illness and grief journey contains voices that
            deserve to be heard.
          </h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {VOICES.map((v, i) => (
            <Reveal
              key={v.n}
              delay={0.06 * i}
              className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}
            >
              <div
                className="h-full bg-ivory border border-line rounded-md p-8 hover:border-gold/60 hover:shadow-[0_20px_40px_-28px_rgba(11,21,34,0.3)] transition-all duration-300"
                data-testid={`facilitators-voice-card-${i + 1}`}
              >
                <span className="font-serif text-gold text-lg">{v.n}</span>
                <h3 className="mt-4 font-serif text-xl sm:text-2xl text-navy leading-snug">
                  {v.title}
                </h3>
                <p className="mt-4 font-serif italic text-gold">
                  &ldquo;{v.quote}&rdquo;
                </p>
                <p className="mt-4 text-stone2 leading-relaxed text-[15px]">
                  {v.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const FRAMEWORK = [
  {
    word: "NOTICE",
    body: "Notice what is already happening—seeing the person and recognizing who or what may be overlooked.",
  },
  {
    word: "LISTEN",
    body: "Listen to hear the person's experience while creating invitation and space without forcing disclosure.",
  },
  {
    word: "UNDERSTAND",
    body: "Discover the story, perspective, relationships, fears, values, beliefs, and circumstances behind the voice—and help meaning emerge without defining it for someone else.",
  },
  {
    word: "RESPOND",
    body: "Determine what the conversation needs and respond without taking over, correcting, fixing, diagnosing, or exceeding the facilitator's role.",
  },
  {
    word: "HONOR",
    body: "Protect personhood, autonomy, dignity, voice, culture, spirituality, privacy, boundaries, and the person's journey.",
  },
];

function Framework() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          A Different Way to Facilitate
        </p>
        <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight">
          Certified facilitators learn a five-part framework:
        </h2>
        <p
          className="mt-8 flex flex-wrap items-center justify-start gap-x-3 gap-y-3"
          data-testid="facilitators-framework-line"
        >
          {FRAMEWORK.map((f, i) => (
            <span key={f.word} className="flex items-center gap-3">
              <span className="font-serif text-lg sm:text-xl text-navy tracking-[0.14em] border border-line-strong rounded-full px-4 py-1.5">
                {f.word}
              </span>
              {i < FRAMEWORK.length - 1 && (
                <span className="text-gold font-serif">→</span>
              )}
            </span>
          ))}
        </p>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
        {FRAMEWORK.map((f, i) => (
          <Reveal key={f.word} delay={0.06 * i} className="h-full">
            <div
              className="h-full border-t-2 border-gold pt-5"
              data-testid={`facilitators-framework-item-${i + 1}`}
            >
              <p className="font-serif text-xl text-navy tracking-[0.1em]">
                {f.word}
              </p>
              <p className="mt-3 text-sm text-stone2 leading-relaxed">
                {f.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <div className="mt-14 max-w-3xl font-serif text-xl sm:text-2xl text-navy leading-relaxed">
          <p>The voices in the room tell facilitators who and what to hear.</p>
          <p className="mt-3">The Five Voices Framework teaches them how to respond.</p>
        </div>
      </Reveal>
    </section>
  );
}

const WHO = [
  "Pastors and ministers",
  "Chaplains and spiritual-care providers",
  "Healthcare and hospice professionals",
  "Bereavement professionals",
  "Caregivers",
  "Volunteers",
  "Support-group leaders",
  "Educators",
  "Community leaders",
  "Others who want to responsibly facilitate the program",
];

function WhoCan() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Who Can Become a Facilitator?
          </p>
          <h2 className="mt-5 max-w-4xl font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight leading-[1.15]">
            Facilitator training is designed for people who want to
            responsibly use the From Diagnosis to Goodbye approach in settings
            such as hospice, healthcare, caregiving, bereavement, ministry,
            senior living, community education, and support groups.
          </h2>
          <p className="mt-10 text-xs uppercase tracking-[0.18em] text-mist">
            Potential facilitators may include
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            {WHO.map((w) => (
              <span
                key={w}
                className="bg-ivory border border-line rounded-full px-5 py-2.5 text-sm text-navy"
              >
                {w}
              </span>
            ))}
          </div>
          <p className="mt-14 max-w-3xl font-serif text-2xl sm:text-3xl text-navy leading-snug" data-testid="facilitators-chaplain-statement">
            You do not have to become a chaplain to become a From Diagnosis to
            Goodbye facilitator.
          </p>
          <p className="mt-6 max-w-3xl text-sm text-stone2 leading-relaxed">
            Certification is specific to facilitating the From Diagnosis to
            Goodbye program and does not independently confer clinical,
            counseling, healthcare, ministry, or professional chaplaincy
            credentials.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

const SESSIONS = [
  {
    name: "Session One — NOTICE",
    sub: "Seeing the Voices Already in the Room",
  },
  {
    name: "Session Two — LISTEN",
    sub: "Creating Space Without Taking Over",
  },
  {
    name: "Session Three — UNDERSTAND",
    sub: "Hearing What Is Said—and What May Be Beneath It",
  },
  {
    name: "Session Four — RESPOND",
    sub: "Responding to What the Conversation Needs",
  },
  {
    name: "Session Five — HONOR",
    sub: "Holding the Person, the Story, and the Boundaries",
  },
];

function Training() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          The Facilitator Training
        </p>
        <p className="mt-4 font-serif italic text-lg text-gold">
          The Five Voices of the Journey
        </p>
        <h2 className="mt-3 max-w-4xl font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight leading-[1.15]">
          Certified Facilitator Training for From Diagnosis to Goodbye
        </h2>
        <p className="mt-8 text-lg text-stone2">
          The certification course consists of five learning sessions:
        </p>
      </Reveal>
      <div className="mt-10">
        {SESSIONS.map((s, i) => (
          <Reveal key={s.name} delay={0.05 * i}>
            <div
              className="grid sm:grid-cols-12 gap-2 sm:gap-8 border-t border-line py-6 last:border-b items-baseline"
              data-testid={`facilitators-session-${i + 1}`}
            >
              <p className="sm:col-span-5 font-serif text-xl text-navy">
                {s.name}
              </p>
              <p className="sm:col-span-7 text-stone2 italic">
                {s.sub}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <p className="mt-10 max-w-3xl text-stone2 leading-relaxed">
          Each session combines teaching, realistic facilitation scenarios,
          reflection, and assessment.
        </p>
        <Link
          to="/facilitators/training"
          data-testid="facilitators-cta-training"
          className={`${ctaCls} mt-8`}
        >
          Explore the Training <ArrowRight className="w-4 h-4" />
        </Link>
      </Reveal>
    </section>
  );
}

function Certification() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                Becoming Certified
              </p>
              <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight leading-[1.15]">
                Certification is earned through preparation and demonstrated
                understanding—not simply by purchasing a course.
              </h2>
              <p className="mt-8 text-stone2 leading-relaxed">
                To become a Certified From Diagnosis to Goodbye Facilitator,
                participants complete the five-session facilitator training,
                required session assessments, and the final Facilitator
                Certification Assessment.
              </p>
              <p className="mt-4 text-stone2 leading-relaxed">
                Participants also affirm the Certified Facilitator Code of
                Ethics and accept the Authorized Facilitator Agreement.
              </p>
              <p className="mt-4 text-stone2 leading-relaxed">
                Successful participants receive an individual certification
                number and a 12-month certification.
              </p>
              <Link
                to="/facilitators/certification"
                data-testid="facilitators-cta-certification"
                className={`${ctaCls} mt-10`}
              >
                View Certification Requirements <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="bg-ivory border border-line rounded-md p-8">
                <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                  Certification Means Authorization
                </p>
                <p className="mt-5 text-stone2 leading-relaxed text-[15px]">
                  The From Diagnosis to Goodbye program, methodology,
                  curriculum, and authorized resources are protected
                  intellectual property.
                </p>
                <p className="mt-4 text-stone2 leading-relaxed text-[15px]">
                  Becoming certified does not transfer ownership of the
                  program.
                </p>
                <p className="mt-4 text-stone2 leading-relaxed text-[15px]">
                  It provides time-limited authorization to responsibly
                  facilitate the approved program according to its standards.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
        <Reveal delay={0.05}>
          <blockquote
            className="mt-14 max-w-4xl border-l-2 border-gold pl-6 sm:pl-8 font-serif italic text-2xl sm:text-3xl text-navy leading-snug"
            data-testid="facilitators-authorization-statement"
          >
            &ldquo;Certification is not ownership. Certification is
            authorization to responsibly carry the work.&rdquo;
          </blockquote>
          <p className="mt-6 text-stone2 leading-relaxed">
            Certified facilitators maintain their authorization through annual
            renewal.
          </p>
          <Link
            to="/facilitators/renewal"
            data-testid="facilitators-cta-renewal"
            className={`${ctaCls} mt-8`}
          >
            Learn About Annual Renewal <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Organizations() {
  return (
    <section className="bg-navy-deep" data-testid="facilitators-org-band">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            For Hospices, Healthcare Organizations, Churches &amp; Community
            Organizations
          </p>
          <h2 className="mt-5 max-w-4xl font-serif text-3xl sm:text-4xl lg:text-5xl text-ivory tracking-tight leading-[1.15]">
            Organizations that want to formally implement From Diagnosis to
            Goodbye may participate through organizational licensing.
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 gap-6 max-w-3xl">
            <div className="border border-gold/40 rounded-md px-7 py-8">
              <p className="font-serif text-2xl sm:text-3xl text-ivory leading-snug">
                A facilitator is <em className="text-gold">certified</em>.
              </p>
            </div>
            <div className="border border-gold/40 rounded-md px-7 py-8">
              <p className="font-serif text-2xl sm:text-3xl text-ivory leading-snug">
                An organization is <em className="text-gold">licensed</em>.
              </p>
            </div>
          </div>
          <p className="mt-10 max-w-3xl text-ivory/70 leading-relaxed">
            Organizational licensing can support implementation within
            hospices, hospitals and healthcare organizations, senior-living
            communities, churches and ministries, bereavement programs,
            caregiver programs, community organizations, and other approved
            settings.
          </p>
          <p className="mt-4 max-w-3xl text-ivory/70 leading-relaxed">
            Individuals delivering the protected program must maintain the
            facilitator credentials required by the program.
          </p>
          <Link
            to="/facilitators/organizations"
            data-testid="facilitators-cta-organizations"
            className={`${ctaGoldCls} mt-10`}
          >
            Explore Organizational Licensing <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Carry() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24 text-center">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Carry the Work Responsibly
        </p>
        <p className="mt-6 max-w-2xl mx-auto font-serif text-2xl sm:text-3xl text-navy leading-snug">
          From Diagnosis to Goodbye began with a simple conviction:
        </p>
        <p className="mt-6 max-w-3xl mx-auto text-lg text-stone2 leading-relaxed">
          People facing serious illness, caregiving, dying, and grief deserve
          to remain more than the circumstances happening around them.
        </p>
        <div className="mt-10 space-y-2 font-serif italic text-2xl sm:text-3xl text-navy">
          <p>Their voices matter.</p>
          <p>Their stories matter.</p>
          <p>Their questions matter.</p>
          <p>Their silence matters.</p>
        </div>
        <p className="mt-10 max-w-2xl mx-auto text-lg text-stone2 leading-relaxed">
          And those entrusted with accompanying them must learn when to
          speak—and when to make room.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="mt-16 bg-sand border border-line rounded-md px-8 sm:px-14 py-14">
          <h2 className="font-serif text-3xl sm:text-4xl text-navy tracking-tight leading-[1.15]">
            Become a Certified From Diagnosis to Goodbye Facilitator
          </h2>
          <p className="mt-4 font-serif italic text-lg text-gold">
            See the person. Hear the voice. Honor the journey.
          </p>
          <Link
            to="/facilitators/training"
            data-testid="facilitators-cta-final"
            className={`${ctaGoldCls} mt-9`}
          >
            Explore Facilitator Training <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Reveal>
    </section>
  );
}

function ComingSoon() {
  return (
    <section
      className="mx-auto max-w-6xl px-5 sm:px-8 pb-10"
      data-testid="facilitators-coming-soon-card"
    >
      <Reveal>
        <div className="max-w-4xl mx-auto">
          <div className="overflow-hidden rounded-md border border-line bg-white shadow-[0_24px_50px_-30px_rgba(11,21,34,0.4)]">
            <img
              src="/images/facilitator-coming-soon.jpg"
              alt="FD2G Coming Soon — Certified Facilitator Training: The Five Voices of the Journey. Notice, Listen, Understand, Respond, Honor. See the person. Hear the voice. Honor the journey."
              className="w-full h-auto"
              loading="lazy"
            />
          </div>
          <p className="mt-6 text-center">
            <Link
              to="/facilitators/training"
              data-testid="facilitators-coming-soon-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-gold transition-colors duration-200"
            >
              Learn More About FD2G Facilitator Training{" "}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </p>
        </div>
      </Reveal>
    </section>
  );
}

export default function Facilitators() {
  return (
    <div className="bg-ivory" data-testid="facilitators-page">
      <Hero />
      <ComingSoon />
      <Voices />
      <Framework />
      <WhoCan />
      <Training />
      <Certification />
      <Organizations />
      <Carry />
    </div>
  );
}
